/**
 * t231u: the phone person sheet's extras. Upcoming shifts (published, anyone can see), and for managers only what the
 * existing endpoints allow: add a shift for them (rota.edit at one of their buildings; the rota's shift sheet with
 * them picked), their time entries this week (timesheet approvers), and employment / positions (employees.edit).
 * Each part is hidden when it's empty or not allowed.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, CalendarClock, CalendarPlus, ChevronRight, Clock, IdCard, Pencil, Plane, Tags, UserPen } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import type { PersonProfile } from '@tradeoutlet/shared';
import { api } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { useLocationsWith } from '@/lib/permissions';
import { useOnline } from '@/lib/runOutbox';
import { ShiftSheet, type ShiftDraft } from '../RotaSheets';
import { EditDetailsSheet } from './EditDetailsSheet';

const ZONE = 'Europe/London';
const dt = (iso: string) => DateTime.fromISO(iso).setZone(ZONE).setLocale('en-GB');
const hm = (iso: string) => dt(iso).toFormat('HH:mm');
const head = 'mb-1 px-5 text-xs font-semibold uppercase tracking-wide text-slate-500';
const row = 'flex min-h-[48px] w-full items-center gap-3 border-b border-slate-100 px-4 text-left text-base last:border-0 active:bg-slate-100 dark:border-slate-800 dark:active:bg-slate-800';

export function UpcomingShifts({ p, onNavigate }: { p: PersonProfile; onNavigate: () => void }) {
  const list = p.today?.upcoming ?? [];
  if (!list.length) return null;
  return (
    <section className="border-t border-slate-100 py-3 dark:border-slate-800" data-testid="person-upcoming">
      <h3 className={head}>Upcoming shifts</h3>
      <ul>
        {list.map(s => (
          <li key={s.starts_at} className="flex h-10 items-center gap-2 px-5 text-sm">
            <span className="w-24 shrink-0 font-medium">{dt(s.starts_at).toFormat('ccc d LLL')}</span>
            <span className="shrink-0 tabular-nums">{hm(s.starts_at)}–{hm(s.ends_at)}</span>
            {s.building && (s.building_id
              ? <Link to={`/buildings/${s.building_id}`} onClick={onNavigate} className="min-w-0 truncate text-slate-600 underline-offset-2 active:underline dark:text-slate-300">{s.building}</Link>
              : <span className="min-w-0 truncate text-slate-600 dark:text-slate-300">{s.building}</span>)}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Timesheets({ p, onNavigate }: { p: PersonProfile; onNavigate: () => void }) {
  const ws = useWorkspaceId();
  const from = DateTime.now().setZone(ZONE).minus({ days: 6 }).toISODate()!;
  const to = DateTime.now().setZone(ZONE).toISODate()!;
  const q = useQuery({ queryKey: ['wf', ws, 'person-timesheets', p.user_id, from], queryFn: () => api.wf.timesheets(ws!, { from, to, user_id: p.user_id, scope: 'manage' }), enabled: Boolean(ws) });
  const entries = [...(q.data?.entries ?? [])].sort((a, b) => b.clock_in_at.localeCompare(a.clock_in_at)).slice(0, 5);
  return (
    <section className="border-t border-slate-100 py-3 dark:border-slate-800" data-testid="person-timesheets">
      <h3 className={head}>Timesheets, last 7 days</h3>
      {!entries.length ? <p className="px-5 text-sm text-slate-500">{q.isLoading ? '…' : 'No time entries.'}</p> : (
        <ul>
          {entries.map(e => (
            <li key={e.id} className="flex h-10 items-center gap-2 px-5 text-sm">
              <span className="w-24 shrink-0 font-medium">{dt(e.clock_in_at).toFormat('ccc d LLL')}</span>
              <span className="tabular-nums">{hm(e.clock_in_at)}–{e.clock_out_at ? hm(e.clock_out_at) : 'now'}</span>
              <span className="ml-auto shrink-0 text-xs capitalize text-slate-500">{e.status}</span>
            </li>
          ))}
        </ul>
      )}
      <Link to="/rota/approvals" onClick={onNavigate} className="mx-5 mt-1 inline-flex h-9 items-center text-sm font-medium underline underline-offset-2">Approvals</Link>
    </section>
  );
}

export function PersonManage({ p, onNavigate }: { p: PersonProfile; onNavigate: (to?: string) => void }) {
  const edit = useLocationsWith('workforce.rota.edit');
  const online = useOnline();
  const [draft, setDraft] = useState<ShiftDraft | null>(null);
  const [editing, setEditing] = useState(false);
  if (p.is_me || p.is_external || !p.active) return null;
  const shiftAt = p.buildings.filter(b => edit === 'all' || edit.includes(b.id));
  const rows = [
    // t513u: name, sign-in email, phone (and job title) for owners and admins.
    p.can.edit_details && { id: 'edit-details', icon: UserPen, label: 'Edit details', go: () => setEditing(true) },
    shiftAt.length > 0 && { id: 'add-shift', icon: CalendarPlus, label: 'Add a shift', go: () => setDraft({ shift: null, date: DateTime.now().setZone(ZONE).toISODate()!, position_id: p.positions[0]?.id ?? null, location_id: shiftAt[0].id, user_id: p.user_id }) },
    // t473u: View rota moved next to Assign them a task (More), for everyone who can see their rota.
    p.can.edit_employment && { id: 'employment', icon: Pencil, label: 'Employment and buildings', go: () => onNavigate(`/settings/people/users/${p.user_id}?tab=employment`) },
    p.can.edit_employment && { id: 'positions', icon: Tags, label: 'Positions', go: () => onNavigate(`/settings/people/users/${p.user_id}?tab=positions`) },
    p.can.edit_employment && { id: 'availability', icon: CalendarClock, label: 'Availability', go: () => onNavigate(`/settings/people/users/${p.user_id}?tab=availability`) },
    // t354u: their balance and absences, for leave approvers / HR over them.
    p.can.view_time_off && { id: 'time-off', icon: Plane, label: 'Time off', go: () => onNavigate(`/workforce/time-off?user=${p.user_id}`) },
    // t368u: their HR record (read-only on a phone, with approvals). Opening it is audited.
    // t714u: Personal details (was HR record), which also holds their date of birth for whoever edits their employment.
    (p.can.view_hr || p.can.edit_employment) && { id: 'hr', icon: IdCard, label: 'Personal details', go: () => onNavigate(`/settings/people/users/${p.user_id}?tab=hr`) },
    p.can.manage_skills && { id: 'skills', icon: BadgeCheck, label: 'Skills & licences', go: () => onNavigate(`/settings/people/users/${p.user_id}?tab=skills`) },
  ].filter(Boolean) as { id: string; icon: typeof Clock; label: string; go: () => void }[];
  if (!rows.length && !p.can.view_timesheets) return null;
  return (
    <>
      {p.can.view_timesheets && <Timesheets p={p} onNavigate={() => onNavigate()} />}
      {rows.length > 0 && (
        <section className="border-t border-slate-100 py-3 dark:border-slate-800" data-testid="person-manage">
          <h3 className={head}>Manage</h3>
          <ul className="mx-5 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
            {rows.map(r => (
              <li key={r.id}><button type="button" onClick={r.go} className={row} data-testid={`person-manage-${r.id}`}><r.icon className="h-5 w-5 text-slate-500" aria-hidden /><span className="flex-1">{r.label}</span><ChevronRight className="h-5 w-5 text-slate-400" aria-hidden /></button></li>
            ))}
          </ul>
        </section>
      )}
      {p.can.edit_details && <EditDetailsSheet open={editing} userId={p.user_id} name={p.display_name} onClose={() => setEditing(false)} />}
      {draft && <ShiftSheet draft={draft} online={online} canEdit buildings={shiftAt} onClose={() => setDraft(null)} />}
    </>
  );
}
